import React, { useState, useEffect } from 'react';
import { C, libFind } from '../constants.jsx';
import { FBStyleSection } from './PanelPrimitives';
import { PageHeaderPanel } from '../page_header/PageHeaderPanel';
import {
  SectionPanel, FlexPanel, GridPanel, FreeDivPanel,
  ColumnsPanel, DividerPanel, StatCardPanel, DataTablePanel,
  BarChartPanel, LineChartPanel, PieChartPanel, BadgePanel,
  InputFieldPanel, DropdownPanel, DatePickerPanel, TextAreaPanel,
  FileUploadPanel, SearchBarPanel, ButtonPanel, TextBlockPanel,
  AlertBoxPanel, EmptyStatePanel, ImageBlockPanel, RichHtmlPanel,
} from './EditorPanels';

const iSt = {
  display: 'block', width: '100%', padding: '7px 9px', fontSize: 12,
  border: `1px solid ${C.border}`, borderRadius: 6, color: C.text,
  background: C.bg, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
};

export default function PropsPanel({
  comp, onChange, onDelete, onDuplicate, allComps,
}) {
  const [tab, setTab] = useState('content');

  useEffect(() => { setTab('content'); }, [comp?.id]);

  if (!comp) return null;

  const isFB = comp.type.startsWith('fb_');
  const isContainerComp = false;

  // Tabs: fb_ types get a "style" tab; container children are now inside content panel
  const tabs = [
    { id: 'content', label: 'Content' },
    ...(isFB ? [{ id: 'style', label: 'Style' }] : []),
  ];

  const handleChange = (patch) => {
  onChange({ ...comp, ...patch });
};

  function renderEditor() {
    const props = { comp, onChange: handleChange, allComps };
    switch (comp.type) {
      case 'page_header':    return <PageHeaderPanel {...props} />;
      case 'section':        return <SectionPanel {...props} />;
      case 'flex_row':
      case 'flex_col':       return <FlexPanel {...props} />;
      case 'grid_container': return <GridPanel {...props} />;
      case 'free_div':       return <FreeDivPanel {...props} />;
      case 'columns':        return <ColumnsPanel {...props} />;
      case 'divider':        return <DividerPanel {...props} />;
      case 'stat_card':      return <StatCardPanel {...props} />;
      case 'data_table':     return <DataTablePanel {...props} />;
      case 'chart_bar':      return <BarChartPanel {...props} />;
      case 'chart_line':     return <LineChartPanel {...props} />;
      case 'chart_pie':      return <PieChartPanel {...props} />;
      case 'badge':          return <BadgePanel {...props} />;
      case 'fb_input':       return <InputFieldPanel {...props} />;
      case 'fb_dropdown':    return <DropdownPanel {...props} />;
      case 'fb_date':        return <DatePickerPanel {...props} />;
      case 'fb_textarea':    return <TextAreaPanel {...props} />;
      case 'fb_file':        return <FileUploadPanel {...props} />;
      case 'search_bar':     return <SearchBarPanel {...props} />;
      case 'btn_primary':
      case 'btn_secondary':
      case 'btn_danger':     return <ButtonPanel {...props} />;
      case 'text_block':     return <TextBlockPanel {...props} />;
      case 'alert_box':      return <AlertBoxPanel {...props} />;
      case 'empty_state':    return <EmptyStatePanel {...props} />;
      case 'image_block':    return <ImageBlockPanel {...props} />;
      case 'rich_html':      return <RichHtmlPanel {...props} />;
      default: return <div style={{ color: C.muted, fontSize: 12, textAlign: 'center', marginTop: 24 }}>No properties.</div>;
    }
  }

  const lib = libFind(comp.type);
  const childCount = 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div style={{
        padding: '11px 13px', borderBottom: `1px solid ${C.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text, display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{ fontSize: 15 }}>{lib.icon}</span>
            {lib.label}
            {isContainerComp && childCount > 0 && (
              <span style={{ fontSize: 11, background: C.accentLt, color: C.accent, borderRadius: 10, padding: '1px 7px', fontWeight: 500 }}>
                {childCount} children
              </span>
            )}
          </div>
          <div style={{ fontSize: 10, color: C.muted, marginTop: 2, fontFamily: 'monospace' }}>{comp.id}</div>
        </div>
        <div style={{ display: 'flex', gap: 3 }}>
          <button title="Duplicate" onClick={onDuplicate} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px 7px', borderRadius: 5, fontSize: 15, color: C.sub }}>⧉</button>
          <button title="Delete" onClick={onDelete} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px 7px', borderRadius: 5, fontSize: 15, color: C.red }}>🗑</button>
        </div>
      </div>

      {/* ── Position & Size (only for root comps, not children) ─────────── */}
      {!comp._isChild && (
        <div style={{ padding: '9px 13px', borderBottom: `1px solid ${C.border}`, background: C.bg, flexShrink: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.sub, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 7 }}>Position & Size</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 6 }}>
            {['x', 'y', 'w', 'h'].map((f) => (
              <div key={f}>
                <label style={{ fontSize: 10, color: C.muted, display: 'block', marginBottom: 3, textAlign: 'center' }}>{f.toUpperCase()}</label>
                <input
                  style={{ ...iSt, textAlign: 'center', fontFamily: 'monospace', padding: '5px 3px' }}
                  type="number"
                  value={comp[f] ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    handleChange({ [f]: raw === '' ? '' : +raw });
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Tabs ────────────────────────────────────────────────────────── */}
      {tabs.length > 1 && (
        <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{
                flex: 1, padding: '9px 4px', fontSize: 12, fontWeight: 600,
                border: 'none', background: 'none', cursor: 'pointer',
                color: tab === t.id ? C.accent : C.sub,
                borderBottom: `2px solid ${tab === t.id ? C.accent : 'transparent'}`,
                fontFamily: 'inherit', transition: 'all .15s', whiteSpace: 'nowrap',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* ── Body ────────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 13 }}>

        {tab === 'style' && isFB && <FBStyleSection comp={comp} onChange={handleChange} />}
        {tab === 'content' && renderEditor()}
      </div>
    </div>
  );
}
